import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  Plus,
  Trash2,
  Copy,
  ListChecks,
  AlertTriangle,
  Building2,
  Target,
  ChevronDown,
  Search,
} from 'lucide-react';
import { amsCheckpointService, CreateAmsCheckpointForm, ICheckpointMeta } from '../../../../services/amsCheckpointService';
import { Asset } from '../../../../types/asset';
import projectTypeService, { ProjectType } from '../../../../services/projectTypeService';
import { assetService } from '../../../../services/assetService';
import documentMasterService from '../../../../services/documentMasterService';
import uomMasterService from '../../../../services/uomMasterService';
import auditTypeService, { AuditType } from '../../../../services/auditTypeService';
import Select from '../../../../shared/components/form/Select';
import Button from '../../../../shared/components/ui/button/Button';
import PageBreadcrumb from '../../../../shared/PageBreadCrumb';
import { Controller } from 'react-hook-form';
import RightDrawer from '../../../../shared/components/form/RightDrawer';

// Brand Colors
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  accent: "#A3CD39",
  dark: "#007F66",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

// Validation schema for AMS bulk form
const schema = yup.object({
  checkpointType: yup.string()
    .default('asset')
    .oneOf(['asset', 'project'], 'Invalid checkpoint level'),
  projectTypeId: yup.string()
    .required('Project Type is required'),
  assetId: yup.string()
    .when('checkpointType', {
      is: 'asset',
      then: (s) => s.required('Please select an asset'),
      otherwise: (s) => s.optional(),
    }),
  items: yup.array().of(
    yup.object({
      name: yup.string()
        .required('Checkpoint name is required')
        .min(2, 'Name must be at least 2 characters')
        .max(200, 'Name must be less than 200 characters'),
      checkpointMeta: yup.object({
        executionType: yup.string().required().oneOf(['manual', 'visual', 'hearing']),
        documentApplicable: yup.boolean().required(),
        documentTypes: yup.array().of(yup.string().trim().max(100)).optional().default([])
          .test('document-types-required', 'At least one document type must be selected when Document Applicable is checked', function(value) {
            const { documentApplicable } = this.parent;
            if (documentApplicable && (!value || value.length === 0)) {
              return this.createError({ message: 'At least one document type must be selected when Document Applicable is checked' });
            }
            return true;
          }),
        designedParameterAvailable: yup.boolean().required(),
        designedParameterCategory: yup.string().when('designedParameterAvailable', {
          is: true,
          then: (schema) => schema.required('Category is required when Designed Parameter Available is checked').trim().max(100),
          otherwise: (schema) => schema.optional().trim(),
        }),
        designedParameterUom: yup.string().when('designedParameterAvailable', {
          is: true,
          then: (schema) => schema.required('Designed Parameter UOM is required when Designed Parameter Available is checked').trim().max(50),
          otherwise: (schema) => schema.optional().trim(),
        }),
        actualParameter: yup.string().when('designedParameterAvailable', {
          is: true,
          then: (schema) => schema.required('Actual Parameter is required when Designed Parameter Available is checked').trim().max(200),
          otherwise: (schema) => schema.optional().trim(),
        }),
        imagesEnabled: yup.boolean().required(),
        remarksEnabled: yup.boolean().required(),
        applicableAuditTypeIds: yup.array().of(yup.string().required()).min(1, 'At least one audit type must be selected').required(),
      }).required(),
    })
  ).min(1, 'At least one checkpoint is required').max(100, 'Maximum 100 checkpoints allowed'),
});

type FormValues = {
  checkpointType: 'asset' | 'project';
  projectTypeId: string;
  assetId?: string;
  items: Array<{
    name: string;
    checkpointMeta: ICheckpointMeta;
  }>;
};

// Document Type Multi-Select Component
interface DocumentTypeMultiSelectProps {
  options: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

const DocumentTypeMultiSelect: React.FC<DocumentTypeMultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter options based on search
  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if all filtered options are selected
  const allFilteredSelected = filteredOptions.length > 0 &&
    filteredOptions.every(opt => selectedValues.includes(opt.value));

  // Check if some filtered options are selected
  const someFilteredSelected = filteredOptions.some(opt =>
    selectedValues.includes(opt.value)
  );

  const handleToggleOption = (value: string) => {
    if (disabled) return;
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    if (allFilteredSelected) {
      // Deselect all filtered options
      const filteredValues = filteredOptions.map(opt => opt.value);
      const newValues = selectedValues.filter(v => !filteredValues.includes(v));
      onChange(newValues);
    } else {
      // Select all filtered options
      const filteredValues = filteredOptions.map(opt => opt.value);
      const newValues = [...new Set([...selectedValues, ...filteredValues])];
      onChange(newValues);
    }
  };

  // Get selected option labels with values
  const selectedOptions = selectedValues
    .map(value => {
      const option = options.find(opt => opt.value === value);
      return option ? { value, label: option.label } : null;
    })
    .filter(Boolean) as Array<{ value: string; label: string }>;

  const handleRemoveTag = (valueToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent dropdown from opening when clicking remove
    const newValues = selectedValues.filter(v => v !== valueToRemove);
    onChange(newValues);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`min-h-12 w-full rounded-xl border bg-white px-4 py-2 text-base text-gray-800 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 border-gray-300 focus:border-brand-500 focus:ring-brand-500/20 flex items-center justify-between ${
          disabled ? 'bg-gray-50 cursor-not-allowed' : ''
        }`}
      >
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0 py-1">
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#339274]/10 text-[#339274] rounded-md text-sm font-medium"
                onClick={(e) => e.stopPropagation()} // Prevent dropdown from opening when clicking tag
              >
                {option.label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => handleRemoveTag(option.value, e)}
                    className="hover:text-red-600 transition-colors flex-shrink-0"
                    aria-label={`Remove ${option.label}`}
                  >
                    <X size={14} />
                  </button>
                )}
              </span>
            ))
          ) : (
            <span className="text-gray-400 py-1">Select document types</span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search document types..."
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Select All / Deselect All */}
          {filteredOptions.length > 0 && (
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
                className="w-full text-left px-3 py-2 text-sm font-medium text-[#339274] hover:bg-[#339274]/10 rounded-lg transition-colors"
              >
                {allFilteredSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          )}

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <div
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleOption(option.value);
                    }}
                    className={`flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${
                      isSelected ? 'bg-[#339274]/5' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleOption(option.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded cursor-pointer"
                    />
                    <label className="ml-3 text-sm text-gray-700 cursor-pointer flex-1">
                      {option.label}
                    </label>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No document types found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AmsBulkCheckpointForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ========== Form State ==========
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [allAssets, setAllAssets] = useState<Asset[]>([]);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [isLoadingProjectTypes, setIsLoadingProjectTypes] = useState(true);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [uomCategories, setUomCategories] = useState<string[]>([]);
  const [activeAuditTypes, setActiveAuditTypes] = useState<AuditType[]>([]);
  const [uomOptionsByIndex, setUomOptionsByIndex] = useState<Record<number, Array<{ value: string; label: string }>>>({});
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);
  const [addDocumentName, setAddDocumentName] = useState('');
  const [addDocumentDescription, setAddDocumentDescription] = useState('');
  const [addDocumentIndex, setAddDocumentIndex] = useState<number | null>(null);

  const defaultCheckpointMeta: ICheckpointMeta = {
    executionType: undefined as any,
    documentApplicable: false,
    documentTypes: [],
    designedParameterAvailable: false,
    designedParameterCategory: '',
    designedParameterUom: '',
    actualParameter: '',
    imagesEnabled: true,
    remarksEnabled: true,
    // Default to applying to every currently-active audit type — a checkpoint left
    // unchecked here would otherwise match none of them and never get auto-picked up
    // by any audit. If the active audit types haven't loaded yet, default to [].
    applicableAuditTypeIds: activeAuditTypes.map((at) => at._id),
  };

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors, isValid, isDirty }
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange" as const,
    defaultValues: {
      checkpointType: 'asset',
      projectTypeId: '',
      assetId: undefined,
      items: [{
        name: '',
        checkpointMeta: defaultCheckpointMeta,
      }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items"
  });

  const watchedCheckpointType = watch('checkpointType');
  const watchedProjectTypeId = watch('projectTypeId');
  const watchedAssetId = watch('assetId');
  const watchedItems = watch('items');

  const areAllAuditTypesChecked = (index: number) => {
    const meta = watchedItems?.[index]?.checkpointMeta;
    if (!meta || activeAuditTypes.length === 0) return false;
    const selectedIds = meta.applicableAuditTypeIds ?? [];
    return activeAuditTypes.every((at) => selectedIds.includes(at._id));
  };
  const toggleAllAuditTypesForRow = (index: number) => {
    const next = !areAllAuditTypesChecked(index);
    setValue(
      `items.${index}.checkpointMeta.applicableAuditTypeIds`,
      next ? activeAuditTypes.map((at) => at._id) : [],
      { shouldDirty: true, shouldValidate: true }
    );
  };

  // ========== Effects ==========

  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        setIsLoadingProjectTypes(true);
        const response = await projectTypeService.getAllProjectTypes();
        let activeProjectTypes: ProjectType[] = [];
        if (response && typeof response === 'object') {
          if ('data' in response && Array.isArray(response.data)) {
            activeProjectTypes = response.data.filter((pt: ProjectType) => pt.status === 'active');
          } else if (Array.isArray(response)) {
            activeProjectTypes = response.filter((pt: ProjectType) => pt.status === 'active');
          }
        }
        setProjectTypes(activeProjectTypes);

        const urlProjectTypeId = searchParams.get('projectTypeId');
        if (urlProjectTypeId && activeProjectTypes.some(pt => (pt._id || pt.id) === urlProjectTypeId)) {
          setValue('projectTypeId', urlProjectTypeId);
        }
      } catch (error) {
        console.error('Failed to fetch project types:', error);
        toast.error('Failed to load project types');
      } finally {
        setIsLoadingProjectTypes(false);
      }
    };

    const fetchDocumentMasters = async () => {
      try {
        const options = await documentMasterService.getDocumentMasterDropdownOptions();
        setDocumentTypeOptions(options);
      } catch (error) {
        console.error('Failed to fetch document masters:', error);
        // Set empty array on error to prevent form from breaking
        setDocumentTypeOptions([]);
      }
    };

    const fetchUomCategories = async () => {
      try {
        const categories = await uomMasterService.getCategories();
        setUomCategories(categories);
      } catch (error) {
        console.error('Failed to fetch UOM categories:', error);
        toast.error('Failed to load UOM categories');
        setUomCategories([]);
      }
    };

    const fetchActiveAuditTypes = async () => {
      try {
        const types = await auditTypeService.getAllActiveAuditTypes();
        setActiveAuditTypes(types);
      } catch (error) {
        console.error('Failed to fetch audit types:', error);
        toast.error('Failed to load audit types');
        setActiveAuditTypes([]);
      }
    };

    fetchProjectTypes();
    fetchDocumentMasters();
    fetchUomCategories();
    fetchActiveAuditTypes();
  }, [searchParams, setValue]);

  // Audit types load asynchronously, so the initial checkpoint's defaultCheckpointMeta
  // is captured with an empty applicableAuditTypeIds. Once the master list arrives,
  // pre-select all active audit types for any checkpoint that still has none — this
  // restores the "applies to all audit types by default" behaviour.
  useEffect(() => {
    if (activeAuditTypes.length === 0) return;
    const allIds = activeAuditTypes.map((at) => at._id);
    const items = getValues('items') || [];
    items.forEach((item, index) => {
      const current = item?.checkpointMeta?.applicableAuditTypeIds ?? [];
      if (current.length === 0) {
        setValue(`items.${index}.checkpointMeta.applicableAuditTypeIds`, allIds, {
          shouldValidate: true,
        });
      }
    });
  }, [activeAuditTypes, getValues, setValue]);

  useEffect(() => {
    const fetchAssets = async () => {
      if (!watchedProjectTypeId) {
        setAssets([]);
        setAllAssets([]);
        setValue('assetId', undefined);
        setAssetSearchQuery('');
        return;
      }

      try {
        setIsLoadingAssets(true);
        const response = await assetService.getAssetsByProjectType(watchedProjectTypeId, { limit: 100 });
        let assetsData: Asset[] = [];
        if (response && typeof response === 'object' && 'assets' in response) {
          assetsData = response.assets;
        } else if (Array.isArray(response)) {
          assetsData = response;
        } else if (response && typeof response === 'object' && 'data' in response) {
          const data = (response as any).data;
          if (Array.isArray(data)) {
            assetsData = data;
          } else if (data && Array.isArray(data.docs)) {
            assetsData = data.docs;
          }
        }

        const activeAssets = assetsData.filter(asset => asset.status === 'active');
        setAllAssets(activeAssets);
        setAssets(activeAssets);

        const urlAssetId = searchParams.get('assetId');
        if (urlAssetId && activeAssets.some(asset => (asset._id || asset.id) === urlAssetId)) {
          setValue('assetId', urlAssetId);
        } else {
          setValue('assetId', undefined);
        }
      } catch (error) {
        console.error('Failed to fetch assets:', error);
        toast.error('Failed to load assets');
        setAssets([]);
        setAllAssets([]);
      } finally {
        setIsLoadingAssets(false);
      }
    };

    fetchAssets();
  }, [watchedProjectTypeId, searchParams, setValue]);

  useEffect(() => {
    if (!assetSearchQuery.trim()) {
      setAssets(allAssets);
      return;
    }

    const query = assetSearchQuery.toLowerCase().trim();
    const filtered = allAssets.filter(asset =>
      asset.name?.toLowerCase().includes(query) ||
      asset.code?.toLowerCase().includes(query)
    );

    setAssets(filtered);
  }, [assetSearchQuery, allAssets]);

  useEffect(() => {
    if (fields.length > 10) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  }, [fields.length]);

  // Warn on browser refresh / tab-close / external navigation when there are
  // unsaved edits (in-app "Cancel" already confirms separately).
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !isSubmitting) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isSubmitting]);

  // ========== Event Handlers ==========

  const handleProjectTypeChange = (projectTypeId: string) => {
    setValue('projectTypeId', projectTypeId, { shouldDirty: true, shouldValidate: true });
      setValue('assetId', undefined);
  };

  const handleAssetChange = (assetId: string) => {
    setValue('assetId', assetId, { shouldDirty: true, shouldValidate: true });
  };

  const fetchUomsByCategory = async (category: string, index: number) => {
    try {
      const options = await uomMasterService.getUomDropdownOptions(category);
      setUomOptionsByIndex(prev => ({ ...prev, [index]: options }));
    } catch (error) {
      console.error('Failed to fetch UOMs:', error);
      toast.error('Failed to load UOMs for selected category');
      setUomOptionsByIndex(prev => ({ ...prev, [index]: [] }));
    }
  };

  const handleCategoryChange = (index: number, category: string) => {
    setValue(`items.${index}.checkpointMeta.designedParameterCategory`, category);
    if (category) {
      fetchUomsByCategory(category, index);
    } else {
      setUomOptionsByIndex(prev => ({ ...prev, [index]: [] }));
      setValue(`items.${index}.checkpointMeta.designedParameterUom`, '');
    }
  };

  const handleAddDocumentClick = (index: number) => {
    setAddDocumentIndex(index);
    setShowAddDocument(true);
    setAddDocumentName('');
    setAddDocumentDescription('');
  };

  const handleAddDocumentSubmit = async () => {
    if (!addDocumentName.trim()) {
      toast.error('Document name is required');
      return;
    }

    try {
      setIsCreatingDocument(true);
      const response = await documentMasterService.createDocumentMaster({
        documentName: addDocumentName.trim(),
        documentDescription: addDocumentDescription.trim() || '',
        status: 'Active',
      });
      
      if (response && response.data) {
        const newDocument = response.data;
        // Add the new document to the options
        const newOption = {
          value: newDocument._id || newDocument.id || '',
          label: newDocument.documentName,
        };
        setDocumentTypeOptions(prev => [...prev, newOption]);
        
        // Auto-select the newly created document in the form for the specific checkpoint
        if (addDocumentIndex !== null) {
          const currentTypes = watchedItems[addDocumentIndex]?.checkpointMeta?.documentTypes || [];
          if (!currentTypes.includes(newOption.value)) {
            setValue(`items.${addDocumentIndex}.checkpointMeta.documentTypes`, [...currentTypes, newOption.value]);
          }
        }
        
        toast.success('Document created successfully');
        setShowAddDocument(false);
        setAddDocumentName('');
        setAddDocumentDescription('');
        setAddDocumentIndex(null);
      }
    } catch (error: any) {
      console.error('Failed to create document:', error);
      toast.error(error?.response?.data?.message || 'Failed to create document');
    } finally {
      setIsCreatingDocument(false);
    }
  };

  const addCheckpoint = () => {
    append({
      name: '',
      checkpointMeta: { ...defaultCheckpointMeta },
    });

    setTimeout(() => {
      const newIndex = fields.length;
      const input = document.querySelector(`input[name="items.${newIndex}.name"]`) as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  };

  const removeCheckpoint = (index: number) => {
    if (fields.length > 1) {
      remove(index);
      toast.success('Checkpoint removed');
    } else {
      toast.warning('At least one checkpoint is required');
    }
  };

  const duplicateCheckpoint = (index: number) => {
    const checkpointToDuplicate = watchedItems[index];
    append({
      name: `${checkpointToDuplicate.name} (Copy)`,
      checkpointMeta: { ...checkpointToDuplicate.checkpointMeta },
    });
    toast.success('Checkpoint duplicated');
  };


  const onSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      setApiError(null);

      const checkpoints: CreateAmsCheckpointForm[] = data.items.map(item => ({
        name: item.name,
        checkpointType: data.checkpointType,
        // Asset id only applies to asset-level checkpoints
        assetId: data.checkpointType === 'asset' ? data.assetId : undefined,
        projectTypeId: data.projectTypeId,
        checkpointMeta: item.checkpointMeta,
        status: 'active',
      }));

      const response = await amsCheckpointService.createBulkAmsCheckpoints({ checkpoints });

      if (response.success) {
        toast.success(`Successfully created ${checkpoints.length} checkpoint${checkpoints.length !== 1 ? 's' : ''}`);
        setTimeout(() => {
          navigate('/masters/checkpoints');
        }, 1000);
      } else {
        setApiError(response.message || 'Failed to create checkpoints');
        toast.error(response.message || 'Failed to create checkpoints');
      }
    } catch (error: any) {
      console.error('Failed to create AMS checkpoints:', error);
      setApiError(error?.response?.data?.message || error.message || 'Failed to create checkpoints');
      toast.error(error?.response?.data?.message || error.message || 'Failed to create checkpoints');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    reset({
      checkpointType: watchedCheckpointType,
      projectTypeId: watchedProjectTypeId,
      assetId: watchedCheckpointType === 'asset' ? watchedAssetId : undefined,
      items: [{
        name: '',
        checkpointMeta: { ...defaultCheckpointMeta },
      }]
    });
    setApiError(null);
    toast.info('Form has been reset');
  };

  const handleCancel = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/masters/checkpoints');
      }
    } else {
      navigate('/masters/checkpoints');
    }
  };

  // ========== Render ==========

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 py-4 mx-auto max-w-[1920px]">
        <PageBreadcrumb
          items={[
            { name: "Masters", path: "/masters" },
            { name: "Checkpoints", path: "/masters/checkpoints" },
            { name: "Bulk Create AMS", path: "/masters/checkpoints/bulk-ams" },
          ]}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200"
            style={{ backgroundColor: BRAND_COLORS.primaryLight }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
                  style={{
                    background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                    borderColor: BRAND_COLORS.primary
                  }}
                >
                  <ListChecks className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    Bulk Create AMS Checkpoints
                  </h1>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Efficiently create multiple AMS checkpoints for your assets or projects
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="font-medium h-9 px-4 text-xs border rounded-lg"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-2" />
                Cancel
              </Button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit as any)} className="p-4 space-y-4">
            {/* Project & Asset Selection */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
              {/* Project Type Selection */}
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2 whitespace-nowrap min-w-fit">
                  <Building2 className="w-3.5 h-3.5" />
                  Project Type
                  <span className="text-red-500 normal-case">*</span>
                </label>

                <div className="flex-1 flex flex-wrap gap-2">
                  {isLoadingProjectTypes ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Loading...
                    </div>
                  ) : (
                    projectTypes.map((pt) => {
                      const isSelected = watchedProjectTypeId === (pt._id || pt.id);
                      return (
                        <button
                          key={pt._id || pt.id}
                          type="button"
                          onClick={() => {
                            setValue('projectTypeId', pt._id || pt.id || '', { shouldDirty: true, shouldValidate: true });
                            handleProjectTypeChange(pt._id || pt.id || '');
                          }}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all duration-200 ${
                            isSelected
                              ? "border-green-500 bg-white text-green-700 shadow-sm ring-1 ring-green-500/20"
                              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {pt.name}
                          {isSelected && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                        </button>
                      );
                    })
                  )}
                </div>

                {errors.projectTypeId && (
                  <div className="flex items-center gap-1.5 text-xs text-red-600 md:ml-auto">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span className="font-medium">Required</span>
                  </div>
                )}
              </div>

              {/* Checkpoint Level Selection (Project vs Asset) */}
              {watchedProjectTypeId && (
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2 whitespace-nowrap min-w-fit">
                    <Target className="w-3.5 h-3.5" />
                    Checkpoint Level
                    <span className="text-red-500 normal-case">*</span>
                  </label>
                  <div className="flex-1 flex flex-wrap gap-2">
                    {([
                      { value: 'project', label: 'Project Type', hint: 'Applies to the whole project type' },
                      { value: 'asset', label: 'Asset', hint: 'Applies to a specific asset' },
                    ] as const).map((opt) => {
                      const isSelected = watchedCheckpointType === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setValue('checkpointType', opt.value, { shouldDirty: true, shouldValidate: true });
                            // Clear asset when switching to project-level so validation/submit are clean
                            if (opt.value === 'project') {
                              setValue('assetId', undefined, { shouldValidate: true });
                            }
                          }}
                          title={opt.hint}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all duration-200 ${
                            isSelected
                              ? "border-green-500 bg-white text-green-700 shadow-sm ring-1 ring-green-500/20"
                              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {opt.label}
                          {isSelected && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Asset Selection */}
                <div className="p-5">
                  {!watchedProjectTypeId ? (
                    <div className="py-8 text-center bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
                      <p className="text-sm font-medium text-gray-400">Select a Project Type to continue</p>
                    </div>
                  ) : watchedCheckpointType === 'project' ? (
                    <div className="py-6 px-4 bg-green-50/40 rounded-lg border border-dashed border-green-200 flex items-start gap-3">
                      <Building2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Project-level checkpoints</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          These checkpoints apply to the whole project type — no asset selection needed.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                          <Target className="w-3.5 h-3.5" />
                          Select Asset
                          <span className="text-red-500 normal-case">*</span>
                          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[10px] font-bold">
                            {assets.length} {assets.length !== allAssets.length && `/ ${allAssets.length}`}
                          </span>
                        </label>
                        {isLoadingAssets && <Loader2 className="w-3.5 h-3.5 animate-spin text-green-600" />}
                      </div>

                      {/* Search Box */}
                      {allAssets.length > 0 && (
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search assets by name or code..."
                            value={assetSearchQuery}
                            onChange={(e) => setAssetSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 pl-9 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all"
                            style={{ fontSize: '13px' }}
                          />
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          {assetSearchQuery && (
                            <button
                              type="button"
                              onClick={() => setAssetSearchQuery('')}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}

                      {assets.length > 0 ? (
                        <>
                          {assetSearchQuery && (
                            <p className="text-xs text-gray-500">
                              {assets.length === allAssets.length
                                ? `Showing all ${assets.length} assets`
                                : `Found ${assets.length} of ${allAssets.length} assets`
                              }
                            </p>
                          )}
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[280px] overflow-y-auto pr-1 custom-scrollbar">
                            {assets.map((asset) => {
                              const isSelected = watchedAssetId === (asset.id || asset._id);
                              return (
                                <button
                                  key={asset.id || asset._id}
                                  type="button"
                                  onClick={() => {
                                    const assetId = asset.id || asset._id;
                                    // Toggle: clicking the already-selected asset deselects it
                                    // (single-select), so the "asset required" state is reachable.
                                    if (isSelected) {
                                      setValue('assetId', undefined, { shouldDirty: true, shouldValidate: true });
                                    } else {
                                      setValue('assetId', assetId, { shouldDirty: true, shouldValidate: true });
                                      handleAssetChange(assetId);
                                    }
                                  }}
                                  className={`group relative flex flex-col p-2.5 rounded-lg border transition-all duration-200 text-left ${
                                    isSelected
                                      ? "border-green-500 bg-green-50/30 ring-1 ring-green-500/20 z-10"
                                      : "border-gray-100 bg-white hover:border-green-300 hover:shadow-sm"
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <Building2 className={`w-3.5 h-3.5 ${isSelected ? 'text-green-600' : 'text-gray-400 group-hover:text-green-500'}`} />
                                    {isSelected && <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />}
                                  </div>
                                  <span className={`text-xs font-bold truncate w-full ${isSelected ? "text-gray-900" : "text-gray-700"}`}>
                                    {asset.name}
                                  </span>
                                  <span className="text-[10px] text-gray-400 truncate w-full">
                                    {asset.code || 'No Code'}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                          <p className="text-xs text-gray-500">
                            {assetSearchQuery
                              ? `No assets found matching "${assetSearchQuery}"`
                              : 'No assets found for this project type'
                            }
                          </p>
                        </div>
                      )}

                      {(!watchedAssetId || errors.assetId) && (
                        <p className="text-xs text-red-600 flex items-center gap-1 mt-2">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {errors.assetId?.message || 'Please select an asset'}
                        </p>
                      )}
                    </motion.div>
                  )}
                </div>
            </div>

            {/* Warning Alert */}
            <AnimatePresence>
              {showWarning && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center gap-3 p-4 rounded-lg border"
                  style={{
                    backgroundColor: BRAND_COLORS.warningLight,
                    borderColor: `${BRAND_COLORS.warning}40`
                  }}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
                    style={{
                      backgroundColor: BRAND_COLORS.warning + '20',
                      borderColor: BRAND_COLORS.warning + '40'
                    }}
                  >
                    <AlertTriangle className="w-5 h-5" style={{ color: BRAND_COLORS.warning }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.warning }}>
                      Large Number of Checkpoints
                    </p>
                    <p className="text-xs mt-1" style={{ color: BRAND_COLORS.warning }}>
                      You're adding {fields.length} checkpoints. Consider breaking this into smaller batches for better performance.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowWarning(false)}
                    className="p-1.5 rounded-lg hover:bg-yellow-100 transition-colors"
                    style={{ color: BRAND_COLORS.warning }}
                  >
                    <X size={16} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Checkpoints List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Checkpoints
                  </h3>
                  <span className="px-3 py-1 text-xs font-medium rounded-full"
                    style={{
                      backgroundColor: BRAND_COLORS.primary + '20',
                      color: BRAND_COLORS.primary
                    }}
                  >
                    {fields.length} {fields.length === 1 ? 'checkpoint' : 'checkpoints'}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCheckpoint}
                  className="font-medium h-9 px-4 text-xs border rounded-lg border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Plus className="w-3.5 h-3.5 mr-2" />
                  Add Checkpoint
                </Button>
              </div>

              <AnimatePresence>
                {fields.map((field, index) => (
                  <motion.div
                    key={field.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center border"
                          style={{
                            backgroundColor: BRAND_COLORS.primary + '20',
                            borderColor: BRAND_COLORS.primary + '40'
                          }}
                        >
                          <ListChecks className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                        </div>
                        <h4 className="text-sm font-medium text-gray-700">
                          Checkpoint {index + 1}
                        </h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => duplicateCheckpoint(index)}
                          className="text-blue-600 border-blue-600 hover:bg-blue-50 font-medium h-8 px-3 text-xs rounded-lg"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Duplicate
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeCheckpoint(index)}
                          className="text-red-600 border-red-600 hover:bg-red-50 font-medium h-8 px-3 text-xs rounded-lg"
                          disabled={fields.length === 1}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Checkpoint Name */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Checkpoint Name *
                        </label>
                        <div className="relative">
                          <input
                            {...register(`items.${index}.name`)}
                            placeholder="Enter checkpoint name"
                            maxLength={200}
                            className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                              errors.items?.[index]?.name
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                                : watchedItems?.[index]?.name && !errors.items?.[index]?.name
                                  ? 'border-green-300 focus:border-green-500 focus:ring-green-500/20'
                                  : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
                            }`}
                          />
                          {watchedItems?.[index]?.name && !errors.items?.[index]?.name && (
                            <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                          )}
                          {errors.items?.[index]?.name && (
                            <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                          )}
                        </div>
                        {errors.items?.[index]?.name && (
                          <p className="flex items-center gap-1 text-sm text-red-600">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {errors.items[index]?.name?.message}
                          </p>
                        )}
                      </div>

                      {/* Checkpoint Meta */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Execution Type */}
                        <div className="space-y-1.5">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Execution Type *
                          </label>
                          <div className="relative">
                            <Controller
                              name={`items.${index}.checkpointMeta.executionType`}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  value={field.value}
                                  onChange={(option) => field.onChange(option?.value || '')}
                                  options={[
                                    { value: 'manual', label: 'Manual' },
                                    { value: 'visual', label: 'Visual' },
                                    { value: 'hearing', label: 'Hearing' },
                                  ]}
                                  placeholder="Select execution type"
                                  className="h-12"
                                />
                              )}
                            />
                            {errors.items?.[index]?.checkpointMeta?.executionType && (
                              <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                            )}
                          </div>
                          {errors.items?.[index]?.checkpointMeta?.executionType && (
                            <p className="flex items-center gap-1 text-sm text-red-600">
                              <AlertCircle className="w-4 h-4 flex-shrink-0" />
                              {errors.items[index]?.checkpointMeta?.executionType?.message}
                            </p>
                          )}
                        </div>

                        {/* Placeholder for grid alignment */}
                        <div></div>

                        {/* Document Applicable */}
                        <div className="flex items-center">
                          <Controller
                            name={`items.${index}.checkpointMeta.documentApplicable`}
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                id={`documentApplicable-${index}`}
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                              />
                            )}
                          />
                          <label htmlFor={`documentApplicable-${index}`} className="ml-2 block text-sm font-semibold text-gray-700">
                            Document Applicable
                          </label>
                        </div>

                        {/* Document Types */}
                        {watchedItems[index]?.checkpointMeta?.documentApplicable && (
                          <div className="col-span-2 space-y-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Document Types *
                            </label>
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                              <Controller
                                name={`items.${index}.checkpointMeta.documentTypes`}
                                control={control}
                                render={({ field }) => (
                                  <DocumentTypeMultiSelect
                                    options={documentTypeOptions}
                                    selectedValues={field.value || []}
                                    onChange={(values) => {
                                      field.onChange(values);
                                    }}
                                  />
                                )}
                              />
                              {errors.items?.[index]?.checkpointMeta?.documentTypes && (
                                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                              )}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleAddDocumentClick(index)}
                                className="h-12 w-12 flex items-center justify-center rounded-xl border border-dashed border-gray-300 hover:border-[#339274] hover:bg-[#339274]/5 text-gray-400 hover:text-[#339274] transition-colors flex-shrink-0"
                                title="Add new document"
                              >
                                <Plus size={20} />
                              </button>
                            </div>
                            {errors.items?.[index]?.checkpointMeta?.documentTypes && (
                              <p className="flex items-center gap-1 text-sm text-red-600">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {errors.items[index]?.checkpointMeta?.documentTypes?.message}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Designed Parameter Available */}
                        <div className="flex items-center">
                          <Controller
                            name={`items.${index}.checkpointMeta.designedParameterAvailable`}
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                id={`designedParameterAvailable-${index}`}
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                              />
                            )}
                          />
                          <label htmlFor={`designedParameterAvailable-${index}`} className="ml-2 block text-sm font-semibold text-gray-700">
                            Designed Parameter Available
                          </label>
                        </div>

                        {/* Category Dropdown - Only show when Designed Parameter Available is checked */}
                        {watchedItems[index]?.checkpointMeta?.designedParameterAvailable && (
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Designed Parameter Category <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Controller
                                name={`items.${index}.checkpointMeta.designedParameterCategory`}
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    value={field.value}
                                    onChange={(option) => handleCategoryChange(index, option?.value || '')}
                                    options={uomCategories.map(cat => ({ value: cat, label: cat }))}
                                    placeholder="Select Category"
                                    className="h-12"
                                  />
                                )}
                              />
                              {errors.items?.[index]?.checkpointMeta?.designedParameterCategory && (
                                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                              )}
                            </div>
                            {errors.items?.[index]?.checkpointMeta?.designedParameterCategory && (
                              <p className="flex items-center gap-1 text-sm text-red-600">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {errors.items[index]?.checkpointMeta?.designedParameterCategory?.message}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Designed Parameter UOM Dropdown - Only show when category is selected */}
                        {watchedItems[index]?.checkpointMeta?.designedParameterAvailable && watchedItems[index]?.checkpointMeta?.designedParameterCategory && (
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Designed Parameter UOM <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Controller
                                name={`items.${index}.checkpointMeta.designedParameterUom`}
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    value={field.value}
                                    onChange={(option) => field.onChange(option?.value || '')}
                                    options={uomOptionsByIndex[index] || []}
                                    placeholder="Select UOM"
                                    className="h-12"
                                  />
                                )}
                              />
                              {errors.items?.[index]?.checkpointMeta?.designedParameterUom && (
                                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                              )}
                            </div>
                            {errors.items?.[index]?.checkpointMeta?.designedParameterUom && (
                              <p className="flex items-center gap-1 text-sm text-red-600">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {errors.items[index]?.checkpointMeta?.designedParameterUom?.message}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Actual Parameter - Text Input */}
                        {watchedItems[index]?.checkpointMeta?.designedParameterAvailable && (
                          <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Actual Parameter <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Controller
                                name={`items.${index}.checkpointMeta.actualParameter`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="text"
                                    placeholder="Enter actual parameter"
                                    maxLength={200}
                                    className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                                      errors.items?.[index]?.checkpointMeta?.actualParameter
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                                        : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500/20'
                                    }`}
                                  />
                                )}
                              />
                              {errors.items?.[index]?.checkpointMeta?.actualParameter && (
                                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500 pointer-events-none" />
                              )}
                            </div>
                            {errors.items?.[index]?.checkpointMeta?.actualParameter && (
                              <p className="flex items-center gap-1 text-sm text-red-600">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {errors.items[index]?.checkpointMeta?.actualParameter?.message}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Images Enabled */}
                        <div className="flex items-center">
                          <Controller
                            name={`items.${index}.checkpointMeta.imagesEnabled`}
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                id={`imagesEnabled-${index}`}
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                              />
                            )}
                          />
                          <label htmlFor={`imagesEnabled-${index}`} className="ml-2 block text-sm font-semibold text-gray-700">
                            Images Enabled
                          </label>
                        </div>

                        {/* Remarks Enabled */}
                        <div className="flex items-center">
                          <Controller
                            name={`items.${index}.checkpointMeta.remarksEnabled`}
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                id={`remarksEnabled-${index}`}
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                              />
                            )}
                          />
                          <label htmlFor={`remarksEnabled-${index}`} className="ml-2 block text-sm font-semibold text-gray-700">
                            Remarks Enabled
                          </label>
                        </div>

                        {/* Audit Type */}
                        <div className="md:col-span-2 pt-2 mt-2 border-t border-gray-100 flex items-center justify-between">
                          <label className="block text-sm font-semibold text-gray-700">
                            Audit Type
                          </label>
                          <button
                            type="button"
                            onClick={() => toggleAllAuditTypesForRow(index)}
                            className="text-xs font-medium text-[#339274] hover:underline"
                          >
                            {areAllAuditTypesChecked(index) ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>

                        {/* Dynamic Audit Types */}
                        {activeAuditTypes.map((auditType) => (
                          <div className="flex items-center" key={auditType._id}>
                            <Controller
                              name={`items.${index}.checkpointMeta.applicableAuditTypeIds`}
                              control={control}
                              render={({ field }) => {
                                const selectedIds: string[] = field.value ?? [];
                                const isChecked = selectedIds.includes(auditType._id);
                                return (
                                  <input
                                    type="checkbox"
                                    id={`auditType-${auditType._id}-${index}`}
                                    checked={isChecked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        field.onChange([...selectedIds, auditType._id]);
                                      } else {
                                        field.onChange(selectedIds.filter((id) => id !== auditType._id));
                                      }
                                    }}
                                    className="h-4 w-4 text-[#339274] focus:ring-[#339274] border-gray-300 rounded"
                                  />
                                );
                              }}
                            />
                            <label htmlFor={`auditType-${auditType._id}-${index}`} className="ml-2 block text-sm font-semibold text-gray-700">
                              {auditType.name}
                            </label>
                          </div>
                        ))}

                        {errors.items?.[index]?.checkpointMeta?.applicableAuditTypeIds && (
                          <p className="md:col-span-2 flex items-center gap-1 text-sm text-red-600">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {errors.items[index]?.checkpointMeta?.applicableAuditTypeIds?.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Add Checkpoint Button */}
              <div className="flex justify-center pt-4">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={addCheckpoint}
                  className="font-medium h-10 px-6 text-sm border rounded-lg border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Checkpoint
                </Button>
              </div>

              {errors.items && (
                <div className="p-4 rounded-lg border"
                  style={{
                    backgroundColor: BRAND_COLORS.errorLight,
                    borderColor: `${BRAND_COLORS.error}40`
                  }}
                >
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" style={{ color: BRAND_COLORS.error }} />
                    <p className="text-sm" style={{ color: BRAND_COLORS.error }}>
                      {errors.items.message || 'Please fix the highlighted errors in your checkpoint(s) before submitting.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* API Error */}
            <AnimatePresence>
              {apiError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center gap-3 p-4 rounded-lg border"
                  style={{
                    backgroundColor: BRAND_COLORS.errorLight,
                    borderColor: `${BRAND_COLORS.error}40`
                  }}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
                    style={{
                      backgroundColor: BRAND_COLORS.error + '20',
                      borderColor: BRAND_COLORS.error + '40'
                    }}
                  >
                    <AlertTriangle className="w-5 h-5" style={{ color: BRAND_COLORS.error }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.error }}>Error</p>
                    <p className="text-xs mt-1" style={{ color: BRAND_COLORS.error }}>{apiError}</p>
                  </div>
                  <button
                    onClick={() => setApiError(null)}
                    className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
                    style={{ color: BRAND_COLORS.error }}
                  >
                    <X size={16} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleReset}
                disabled={isSubmitting}
                className="w-full sm:w-auto order-2 sm:order-1 font-medium h-10 px-6 text-sm border rounded-lg"
              >
                Reset Form
              </Button>
              <Button
                type="submit"
                variant="sorigin"
                size="lg"
                disabled={!isValid || isSubmitting || !watchedProjectTypeId || (watchedCheckpointType === 'asset' && !watchedAssetId)}
                className="w-full sm:w-auto min-w-[180px] order-1 sm:order-2 font-medium h-10 px-6 text-sm rounded-lg"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Checkpoints...
                  </div>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create {fields.length} Checkpoint{fields.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>

      {/* Add Document Form - Right Drawer for bulk-ams page */}
      <RightDrawer
        open={showAddDocument}
        onClose={() => {
          setShowAddDocument(false);
          setAddDocumentName('');
          setAddDocumentDescription('');
          setAddDocumentIndex(null);
        }}
        title="Add New Document"
        size="md"
        footer={
          <div className="flex justify-end gap-3 px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddDocument(false);
                setAddDocumentName('');
                setAddDocumentDescription('');
                setAddDocumentIndex(null);
              }}
              disabled={isCreatingDocument}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddDocumentSubmit}
              disabled={isCreatingDocument || !addDocumentName.trim()}
              className="bg-[#339274] hover:bg-[#29795f] text-white"
            >
              {isCreatingDocument ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Document'
              )}
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Document Name *
            </label>
            <input
              type="text"
              value={addDocumentName}
              onChange={(e) => setAddDocumentName(e.target.value)}
              placeholder="Enter document name"
              maxLength={200}
              className="h-12 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 focus:border-[#339274] focus:ring-[#339274]/20"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Document Description
            </label>
            <textarea
              value={addDocumentDescription}
              onChange={(e) => setAddDocumentDescription(e.target.value)}
              placeholder="Enter document description (optional)"
              maxLength={500}
              rows={4}
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 focus:border-[#339274] focus:ring-[#339274]/20 resize-none"
            />
          </div>
        </div>
      </RightDrawer>
    </div>
  );
}
